import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { AlertDialog } from "common/components/AlertDialog";
import { isLandlordTypeGuard } from "common/models/userInfo";
import { DeviceType } from "common/types";
import { TwoFactorStepper } from "meter/components/2Factor/twoFactorStepper";
import { RemovePhoneNumberDialog } from "meter/components/removePhoneNumberDialog";
import {
  getAllMetersAction,
  toggleAllMetersAction,
  toggleMeterAction,
} from "meter/meter.actions";
import { removePhoneNumberAction } from "meter/twoFactor.actions";
import { IMeterObject } from "meter/types";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IApplicationState } from "redux/root-reducer";
import { createSelector } from "reselect";
import {
  getAllSensorDataForSingleNode,
  toggleAllSensorsAction,
  toggleSingleSensorAction,
} from "safetySensors/Sensors.actions";
import { ISensorData, ISensorOrMeterIdentifier } from "safetySensors/types";
import { NotificationItem } from "./NotificationItem";
import { PhoneNumbers } from "./PhoneNumbers";
import styles from "./Profile.module.scss";

const notificationsSelector = createSelector(
  (state: IApplicationState) => state.common,
  (state: IApplicationState) => state.sensors,
  (state: IApplicationState) => state.meters,
  (common, sensors, meters) => ({
    id: isLandlordTypeGuard(common.currentUserInfo)
      ? null
      : common.currentUserInfo.NodeID,
    domainCode: common.selectedDomain?.Code || "",
    phoneNumbers: common.currentUserInfo.PhoneNumbers,
    sensorMonitoringOn: sensors.monitoringOn,
    sensors: sensors.allSensorDataForSingleNode.filter(
      (s) =>
        s.DeviceType === DeviceType.Leakage || s.DeviceType === DeviceType.Smoke
    ),
    meterMonitoringOn: meters.monitoringOn,
    meters: meters.meters,
    userIsReady: common.userIsReady,
  })
);

export const ManageNotifications = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [twoFactorOpen, setTwoFactorOpen] = useState<boolean>(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [sensorOrMeterIdentifier, setSensorOrMeterIdentifier] =
    useState<ISensorOrMeterIdentifier>();
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);
  const {
    id,
    domainCode,
    phoneNumbers,
    sensorMonitoringOn,
    sensors,
    meterMonitoringOn,
    meters,
  } = useSelector(notificationsSelector);

  const shouldOpenAlert = sensors.some((s) => s.Alert);

  const alertNodes = [
    ...sensors
      .filter((s) => s.Alert)
      .map((s) => `${s.MeasuringPointName}: ${s.Alert?.Description || ""}`),
    ...meters.filter((m) => m.Status).map((m) => `${m.Name} ${t("is open!")}`),
  ];

  const onRemove = async (phoneNumber: string) => {
    dispatch(removePhoneNumberAction(phoneNumber));
    setConfirmationDialogOpen(false);
  };

  const alertClose = (activatecallback: boolean) => {
    setAlertDialogOpen(false);
    if (activatecallback && sensorOrMeterIdentifier) {
      if (
        !sensorOrMeterIdentifier.allMeters &&
        sensorOrMeterIdentifier.type === "Shell Protection Meter"
      ) {
        const meter = meters.find(
          (m) => m.PositionID === sensorOrMeterIdentifier.identifier
        );
        if (!meter) return;
        dispatch(toggleMeterAction(domainCode, meter, true));
        return;
      }
      if (
        !sensorOrMeterIdentifier.allMeters &&
        sensorOrMeterIdentifier.type === "Safety Sensor"
      ) {
        const sensor = sensors.find(
          (s) => +s.MeasuringDeviceID === sensorOrMeterIdentifier.identifier
        );
        if (!sensor) return;
        dispatch(toggleSingleSensorAction(domainCode, sensor, true));
      }
    }
    if (
      activatecallback &&
      sensorOrMeterIdentifier &&
      sensorOrMeterIdentifier.allMeters
    )
      sensorOrMeterIdentifier.type === "Safety Sensor"
        ? dispatch(toggleAllSensorsAction(domainCode, true))
        : dispatch(toggleAllMetersAction(domainCode, true));
    setAlertDialogOpen(false);
  };

  const toggleAllSensors = (checked: boolean) => {
    const identifier: ISensorOrMeterIdentifier = {
      type: "Safety Sensor",
      allMeters: true,
      identifier: undefined,
    };
    setSensorOrMeterIdentifier(identifier);
    if (!phoneNumbers || phoneNumbers.length < 1) {
      setTwoFactorOpen(true);
      return;
    }

    if (!checked) {
      dispatch(toggleAllSensorsAction(domainCode, checked));
      return;
    } else if (checked && shouldOpenAlert) {
      setAlertDialogOpen(true);
    } else {
      dispatch(toggleAllSensorsAction(domainCode, checked));
    }
  };

  const toggleSingleSensor = (checked: boolean, sensor: ISensorData) => {
    const identifier: ISensorOrMeterIdentifier = {
      type: "Safety Sensor",
      allMeters: false,
      identifier: +sensor.MeasuringDeviceID,
    };
    setSensorOrMeterIdentifier(identifier);
    if (!phoneNumbers || !phoneNumbers.length) {
      setTwoFactorOpen(true);
      return;
    }

    if (checked && sensors.filter((s) => s.Alert).length >= 1) {
      setAlertDialogOpen(true);
      return;
    }

    dispatch(toggleSingleSensorAction(domainCode, sensor, checked));
  };

  const toggleAllMeters = (checked: boolean) => {
    const identifier: ISensorOrMeterIdentifier = {
      type: "Shell Protection Meter",
      allMeters: true,
      identifier: undefined,
    };
    setSensorOrMeterIdentifier(identifier);
    if (!phoneNumbers || phoneNumbers.length < 1) {
      setTwoFactorOpen(true);
      return;
    }
    if (!checked) {
      dispatch(toggleAllMetersAction(domainCode, checked));
      return;
    } else if (checked && shouldOpenAlert) {
      setAlertDialogOpen(true);
    } else {
      dispatch(toggleAllMetersAction(domainCode, checked));
    }
  };

  const toggleSingleMeter = (checked: boolean, meter: IMeterObject) => {
    const identifier: ISensorOrMeterIdentifier = {
      type: "Shell Protection Meter",
      allMeters: false,
      identifier: +meter.PositionID,
    };
    setSensorOrMeterIdentifier(identifier);
    if (!phoneNumbers || !phoneNumbers.length) {
      setTwoFactorOpen(true);
      return;
    }

    if (checked && meters.filter((m) => m.Status).length >= 1) {
      setAlertDialogOpen(true);
      return;
    }
    dispatch(toggleMeterAction(domainCode, meter, checked));
  };

  useEffect(() => {
    if (!id) return;
    !sensors.length &&
      dispatch(getAllSensorDataForSingleNode(domainCode, parseInt(id)));

    meters.length < 1 && dispatch(getAllMetersAction(domainCode, id));
  }, [id]);

  return (
    <Box className={styles.container}>
      <Typography variant="h1">{t("Manage notifications")}</Typography>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h2">{t("Notifications")}</Typography>
        </AccordionSummary>
        <AccordionDetails
          style={{ display: "flex", gap: 15, flexDirection: "column" }}
        >
          <NotificationItem
            text={t("Safety")}
            checked={sensorMonitoringOn}
            onToggle={(checked) => toggleAllSensors(checked)}
          />
          {sensors.map((sensor) => (
            <NotificationItem
              key={sensor.MeasuringDeviceID}
              inset
              text={sensor.MeasuringPointName}
              checked={!!sensor.Monitoring}
              onToggle={(checked) => toggleSingleSensor(checked, sensor)}
            />
          ))}

          <NotificationItem
            text={t("Shell protection")}
            checked={meterMonitoringOn}
            onToggle={(checked) => toggleAllMeters(checked)}
          />

          {meters.map((meter) => (
            <NotificationItem
              key={meter.Name}
              inset
              text={meter.Name}
              checked={meter.Monitoring}
              onToggle={(checked) => toggleSingleMeter(checked, meter)}
            />
          ))}
        </AccordionDetails>
      </Accordion>

      <Button
        onClick={() => setTwoFactorOpen(true)}
        color="primary"
        variant="contained"
        style={{ alignSelf: "flex-end" }}
      >
        {t("Add phone number")}
      </Button>

      <Typography variant="h2">{t("Added phone numbers")}</Typography>

      <PhoneNumbers
        onEdit={(number) => {
          setSelectedNumber(number);
          setTwoFactorOpen(true);
        }}
        onRemove={(number) => {
          setSelectedNumber(number);
          setConfirmationDialogOpen(true);
        }}
      />

      <TwoFactorStepper
        open={twoFactorOpen}
        onClose={() => setTwoFactorOpen(false)}
        initialStep={2}
        phoneNumberToDelete={selectedNumber || undefined}
      />
      <RemovePhoneNumberDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onRemove={() => selectedNumber && onRemove(selectedNumber)}
      />

      <AlertDialog
        open={alertDialogOpen}
        openNodes={alertNodes}
        activateTitle={t("Activate")}
        onClose={alertClose}
      />
    </Box>
  );
};
